import { useEffect, useMemo, useRef, useState } from 'react'
import { Screen, Panel, PrimaryButton, GhostButton, OfflineChip } from '../components/ui.jsx'
import { Pin, Check, Wave, CloudOff } from '../components/icons.jsx'
import { createCaptureQueue } from '../data/queue.js'
import { startDay, linkRecordId } from '../data/day.js'
import { pendingReason } from '../../shared/captureMapping.js'
import { todayISO } from '../data/workdate.js'
import './checkin.css'

/*
 * Morning Check-In — the Ten Second rule: who, where, when.
 *
 * The project is an explicit choice (product decision from field testing,
 * replacing assignment-based inference): "Which project are you working on
 * today?" over the approved project list, one tap. A missing project can be created on the spot — marked
 * Pending Approval for the office to approve, rename, or merge later. Check-In
 * NEVER blocks: no selection, no network, no matched project — the capture
 * still succeeds (held to the Pending Desk when unresolved).
 * Duties/assignments belong to later operational workflows, not this screen.
 */
export default function CheckIn({ adapter, session, onDone }) {
  const [projects, setProjects] = useState(null)   // null = loading, [] = none/unavailable
  const [loadFailed, setLoadFailed] = useState(false)
  const [chosen, setChosen] = useState(null)       // { id|null, name, pendingApproval? }
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState(false)
  const [dupSession, setDupSession] = useState(null) // active session found server-side
  const [assignment, setAssignment] = useState(null)   // BUILD 001: today's expected Project, if any
  const [online, setOnline] = useState(navigator.onLine)
  const gpsRef = useRef(null) // filled silently; never awaited, never required

  const queue = useMemo(
    () => createCaptureQueue(adapter, { onCheckinSynced: linkRecordId }),
    [adapter],
  )
  useEffect(() => () => queue.dispose(), [queue])

  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down) }
  }, [])

  // Silent, best-effort GPS. Informational only — never blocking.
  useEffect(() => {
    if (!('geolocation' in navigator)) return
    navigator.geolocation.getCurrentPosition(
      (pos) => { gpsRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy } },
      () => { /* denied or unavailable */ },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 120000 },
    )
  }, [])

  useEffect(() => {
    let alive = true
    adapter.listProjects(session.tenant?.id)
      .then((p) => { if (alive) setProjects(p) })
      .catch(() => { if (alive) { setProjects([]); setLoadFailed(true) } })
    return () => { alive = false }
  }, [adapter, session.tenant])

  // BUILD 001 — prefill today's Project Assignment (Employee Home Experience
  // v1.0 A6: "state the inference rather than asking"). It only preselects;
  // the list stays open, so tapping another project IS the Change affordance.
  // The Assignment record itself is never written by this screen — what the
  // worker chooses is the REPORTED project, a separate fact.
  const employeeId = session.employee?.id ?? null
  useEffect(() => {
    let alive = true
    if (!employeeId || typeof adapter.getAssignmentForDate !== 'function') return
    adapter.getAssignmentForDate(employeeId, todayISO())
      .then((a) => {
        if (!alive || !a) return
        setAssignment(a)
        setChosen((c) => c ?? { id: a.project.id, name: a.project.name, location: a.project.location, pendingApproval: false })
      })
      .catch(() => { /* no assignment or lookup failed — the list still works */ })
    return () => { alive = false }
  }, [adapter, employeeId])

  const firstName = session.employee?.name?.split(' ')[0] ?? 'there'

  async function addProject() {
    const name = newName.trim().replace(/\s+/g, ' ')
    if (!name || creating) return
    setCreating(true)
    try {
      const project = await adapter.createProject(session.tenant?.id, name, session.employee?.id ?? null)
      setProjects((p) => [...(p ?? []), project])
      setChosen(project)
    } catch {
      // Offline or backend down: continue with a self-reported project name —
      // the held capture carries it for the office to resolve. Never blocks.
      setChosen({ id: null, name, pendingApproval: true, selfReported: true })
    }
    setCreating(false)
    setAdding(false)
    setNewName('')
  }

  async function checkIn() {
    if (busy) return
    setBusy(true)
    const capturedAt = new Date().toISOString()
    const fullSession = { ...session, project: chosen, gps: gpsRef.current ?? null }
    const result = await queue.captureCheckIn(fullSession, capturedAt)
    // One Active Workday guard: the server found an active session this
    // device doesn't hold. Surface it plainly — project and check-in time —
    // and create nothing. No day record starts; the person stays here.
    if (result.duplicate) {
      setDupSession(result.activeSession ?? {})
      setBusy(false)
      return
    }
    // FD-001: the day starts on this device — status Working. `held` is
    // computed locally (same rule the backend applies) so an offline check-in
    // knows its own path before it ever syncs.
    startDay({
      captureId: result.captureId,
      recordId: result.syncStatus === 'saved' && !result.held ? result.recordId : null,
      held: result.held ?? pendingReason(fullSession) !== null,
      checkedInAt: capturedAt,
      session: fullSession,
    })
    onDone(result)
  }

  return (
    <Screen right={!online ? <OfflineChip><CloudOff size={15} /> Offline</OfflineChip> : null}>
      {/* Personalized, time-neutral greeting — identity is known here. No
          time-of-day variants (morning/afternoon/timezone complexity adds
          nothing; product decision from real testing). */}
      <div className="stack" style={{ marginTop: 'var(--sp-4)' }}>
        <h1 className="title-xl rise" style={{ animationDelay: '60ms' }}>
          Welcome,<br /><span className="accent">{firstName}</span>
        </h1>
      </div>

      <Panel className="project-panel rise" style={{ animationDelay: '200ms' }}>
        {/* The project is the business object; physical location is one of its
            attributes and may change independently — so we ask for the project. */}
        {assignment && (
          <p className="meta assignment-hint rise" style={{ marginBottom: 'var(--sp-1)' }}>
            Today&rsquo;s assignment: <strong>{assignment.project.name}</strong>
            {chosen?.id === assignment.project.id ? ' — tap another project to change.' : ' — you chose a different project.'}
          </p>
        )}
        <p className="where-question">Which project are you working on today?</p>

        {projects === null ? (
          <p className="meta">Loading projects…</p>
        ) : (
          <div className="project-list" role="listbox" aria-label="Which project are you working on today?">
            {projects.map((p) => (
              <button
                key={p.id}
                className={`project-option${chosen?.id === p.id ? ' project-option--chosen' : ''}`}
                role="option"
                aria-selected={chosen?.id === p.id}
                onClick={() => setChosen(p)}
              >
                <Pin size={16} />
                <span className="project-option__text">
                  {p.name}
                  {p.pendingApproval && <span className="project-pending-tag">pending approval</span>}
                </span>
                {chosen?.id === p.id && <Check size={18} />}
              </button>
            ))}

            {chosen?.selfReported && (
              <button className="project-option project-option--chosen" aria-selected="true" role="option">
                <Pin size={16} />
                <span className="project-option__text">
                  {chosen.name}
                  <span className="project-pending-tag">will be added when back online</span>
                </span>
                <Check size={18} />
              </button>
            )}

            {projects.length === 0 && !loadFailed && !chosen && (
              <p className="meta">No projects yet — add the one you’re on.</p>
            )}
            {loadFailed && (
              <p className="meta">
                Can’t load projects right now. Add yours below, or just check in —
                the office will match it up.
              </p>
            )}
          </div>
        )}

        {adding ? (
          <form className="project-add" onSubmit={(e) => { e.preventDefault(); addProject() }}>
            <input
              className="project-add__input"
              type="text"
              placeholder="Project name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              aria-label="New project name"
              autoFocus
            />
            <div className="project-add__actions">
              <GhostButton type="button" onClick={() => { setAdding(false); setNewName('') }} disabled={creating}>
                Cancel
              </GhostButton>
              <GhostButton type="submit" disabled={!newName.trim() || creating}>
                {creating ? 'Adding…' : 'Add project'}
              </GhostButton>
            </div>
          </form>
        ) : (
          projects !== null && (
            <GhostButton className="project-add-cta" onClick={() => setAdding(true)}>
              + My project isn’t listed
            </GhostButton>
          )
        )}
      </Panel>

      <div className="spacer" />

      <PrimaryButton className="btn--block checkin-btn rise" style={{ animationDelay: '320ms' }}
                     onClick={checkIn} disabled={busy || !!dupSession} aria-label="Check In">
        <Wave size={24} /> {busy ? 'Checking in…' : 'Check In'}
      </PrimaryButton>
      {dupSession && (
        <p className="meta" role="status" style={{ textAlign: 'center', marginTop: 'var(--sp-1)' }}>
          You’re already checked in
          {dupSession.projectName ? ` — ${dupSession.projectName}` : ''}
          {dupSession.checkedInAt
            ? `, since ${new Date(dupSession.checkedInAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
            : ''}.
          Check out there before starting a new day — the office can help if this doesn’t look right.
        </p>
      )}
      {!chosen && projects !== null && !dupSession && (
        <p className="meta rise" style={{ textAlign: 'center', marginTop: 'var(--sp-1)' }}>
          Not sure? Check in anyway — the office will match it up.
        </p>
      )}
      {!online && (
        <p className="meta rise" style={{ textAlign: 'center', marginTop: 'var(--sp-1)' }}>
          Saved on your device, will sync automatically.
        </p>
      )}
    </Screen>
  )
}
